import MoveToStudyHeader from '@/components/Modals/MoveToStudy/MoveToStudyHeader';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Radio from '@/components/UI/FormElements/Radio/index';
import KeywordSearch from '@/components/UI/KeywordSearch';
import Pagination from '@/components/UI/Pagination';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { CageApiId } from '@/model/Cage.model';
import type { Study } from '@/model/Study.model';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import * as Auth from '@/support/auth';
import { useDebounce } from '@/support/Hooks';
import { useAbortController } from '@/support/Hooks/fetch/useAbortController';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { ModalActions, ModalContainer, useModalAction } from '@/utils/modal';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors.tsx';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Animal, AnimalApiId } from '@/model/Animal.model.ts';
import { RouteService } from '@/support/RouteService.ts';

interface MoveToStudyTableProps {
  cageApiIds: Array<CageApiId>;
  animals: Array<Animal>;
  currentStudy: Study;
  animalCount: number;
  handleCallback: (
    cageApiIds: Array<CageApiId>,
    currentStudy: Study,
    selectedStudy: Study | undefined,
    animalCount: number
  ) => void;
  moveToStudyConfirmationV2Callback: (
    targetStudy: Study | undefined,
    sourceStudy: Study,
    animal_api_ids: AnimalApiId[],
    animalCageNames: Set<string>
  ) => void;
}

interface PaginationProps {
  total_pages: number;
  current_page: number;
  per_page: number;
}

interface SortProps {
  order: string;
  sort: string;
}

const moveAnimalsV2Selector = createSelector([featuresSelector], (features) => ({
  moveAnimalsV2: features?.move_animals_study_v2 ?? false,
}));

const MoveToStudyList: FC<MoveToStudyTableProps> = ({
  cageApiIds,
  animals,
  currentStudy,
  animalCount,
  handleCallback,
  moveToStudyConfirmationV2Callback,
}) => {
  const { closeModal } = useModalAction();
  const { moveAnimalsV2 } = useSelector(moveAnimalsV2Selector);
  const navigate = useNavigate();
  const isMounted = useMountedState();
  const [selectedStudy, setSelectedStudy] = useState<Study>();
  const [studies, setStudies] = useState<Array<Study>>([]);
  const [query, setQuery] = useState('');
  const { newAbortController } = useAbortController();
  const [pagingProps, setPagingProps] = useState<PaginationProps>({ total_pages: 0, current_page: 1, per_page: 10 });
  const [sortProps, setSortProps] = useState<SortProps>({ sort: 'created_at', order: 'desc' });

  const { sort, order } = sortProps;

  const debouncedSearchQuery = useDebounce(query, 500);
  useEffect(() => {
    setPagingProps((previous) => ({
      total_pages: previous?.total_pages ?? pagingProps.total_pages,
      current_page: 1,
      per_page: previous?.per_page ?? pagingProps.per_page,
    }));

    fetchStudies(pagingProps.current_page, pagingProps.per_page, sortProps.sort, sortProps.order);
  }, [debouncedSearchQuery]);

  const handlePagination = (data: Pick<PaginationProps, 'per_page' | 'current_page'>) => {
    setPagingProps({
      per_page: data.per_page,
      current_page: data.current_page,
      total_pages: pagingProps.total_pages,
    });

    fetchStudies(data.current_page, data.per_page, sortProps.sort, sortProps.order);
  };

  const handleSort = (value: any) => {
    setSortProps({ sort: value.sort, order: value.order });

    fetchStudies(pagingProps.current_page, pagingProps.per_page, value.sort, value.order);
  };

  const fetchStudies = async (currentPage: number, perPage: number, sort: string, order: string) => {
    try {
      const response = await Http.get<Record<string, any>>(
        apiRoute(
          'studies.active',
          null,
          {
            ...(_isNotEmpty(debouncedSearchQuery) ? { query: debouncedSearchQuery } : {}),
            perPage,
            page: currentPage,
            sort,
            order,
            filterType: 'and',
          },
          `${
            moveAnimalsV2
              ? ''
              : `filters[]=type|eq|${
                  currentStudy.settings?.id == null ? 'null' : encodeURIComponent(currentStudy.settings.title)
                }&`
          }filters[]=access|eq|write`
        ),
        { signal: newAbortController().signal }
      );

      if (isMounted()) {
        setPagingProps((previous) => ({
          total_pages: response.data.meta.pagination.total_pages,
          current_page: previous?.current_page ?? pagingProps.current_page,
          per_page: previous?.per_page ?? pagingProps.per_page,
        }));

        setStudies(response.data.data);
      }
    } catch (error: any) {
      // intentionally empty
    }
  };

  const onSelectStudy = (e: React.ChangeEvent<HTMLInputElement>) => {
    const study = studies.find((s) => s.id === Number(e.target.value));
    setSelectedStudy(study);
  };

  const isRowDisabled = (study: Study) => {
    const isSameStudy = study.id === currentStudy.id;
    const isNotWriteUser = !Auth.isWriteUserForStudy(study);
    if (moveAnimalsV2) {
      return isSameStudy || isNotWriteUser;
    }
    return isSameStudy || isNotWriteUser || study?.settings?.id !== currentStudy.settings?.id;
  };

  const columns: Array<TanStackColumnDef<Study>> = [
    {
      id: 'name',
      header: 'Study name',
      accessorKey: 'name',
      enableSorting: true,
      cell: ({ row }: { row: { original: Study; index: number } }) => {
        const { original, index } = row;
        const { id, name } = original;
        return (
          <div className="flex flex-wrap items-center">
            <Radio
              id={id as string}
              name="move-to-study-select"
              onChange={onSelectStudy}
              checked={selectedStudy?.id === Number(id)}
              value={id as string}
              label={name}
              disabled={isRowDisabled(original)}
              className="mb2"
              index={index}
              key={id as string}
            />
          </div>
        );
      },
      isVisible: true,
    },
    {
      id: 'code',
      header: 'Study code',
      accessorKey: 'code',
      enableSorting: true,
      isVisible: false,
      cell: ({ row }: { row: { original: Study } }) => (
        <span className={isRowDisabled(row.original) ? 'dark-gray' : ''}>{row.original.code}</span>
      ),
    },
    {
      id: 'preset',
      header: 'Preset',
      accessorFn: (row: Study) => row.settings?.title,
      enableSorting: true,
      cell: ({ row }: { row: { original: Study } }) => (
        <span className={isRowDisabled(row.original) ? 'dark-gray' : ''}>{row.original.settings.title}</span>
      ),
      isVisible: true,
    },
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'created_at',
      enableSorting: true,
      cell: ({ row }: { row: { original: Study } }) => (
        <span className={isRowDisabled(row.original) ? 'dark-gray' : ''}>
          <DateTimeRenderer value={row.original.created_at} />
        </span>
      ),
      isVisible: true,
    },
  ];

  const updatedColumns = studies.some((study) => _notNil(study?.code))
    ? columns.map((col) => (col.id === 'code' ? { ...col, isVisible: true } : col))
    : columns;

  const handleFormSubmit = async () => {
    if (moveAnimalsV2) {
      const animalApiIds = animals.map(({ api_id }) => api_id) as AnimalApiId[];
      // Filter any potential null values
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      const animalCageNames = new Set(animals.map(({ cage }) => cage?.name).filter((name): name is string => !!name));

      // If no selected animal has a measurement, then the mapping is not required
      const mappingNotRequired = animals.every((animal) => _isNil(animal.latestMeasurement));
      if (mappingNotRequired) {
        moveToStudyConfirmationV2Callback(selectedStudy, currentStudy, animalApiIds ?? [], animalCageNames);
      } else {
        closeModal();
        navigate(RouteService.web({ route: 'studies.move.animals', path: { studyId: currentStudy.id } }).route, {
          state: {
            sourceStudy: currentStudy,
            targetStudy: selectedStudy,
            animalApiIds: animalApiIds,
            animalCageNames: animalCageNames,
          },
        });
      }
    } else {
      handleCallback(cageApiIds, currentStudy, selectedStudy, animalCount);
    }
  };

  return (
    <ModalContainer size="medium" className="bg-white h-100">
      <MoveToStudyHeader cageCount={cageApiIds.length} animalCount={animalCount} />
      <div className="pa3 bb b--moon-gray">
        <KeywordSearch
          style={{ width: '22rem' }}
          searchQuery={query}
          searchPlaceholderText="Search by study name or code"
          setSearchQuery={setQuery}
          autoFocus={true}
        />
      </div>
      <Table
        noDataMessage="No active studies"
        data={studies ?? []}
        columns={updatedColumns}
        manualSort={{ sort, order }}
        setManualSort={handleSort}
      />
      <Pagination
        current_page={pagingProps.current_page}
        total_pages={pagingProps.total_pages}
        per_page={pagingProps.per_page}
        handleChange={handlePagination}
      />
      <ModalActions
        submitBtnText="Continue"
        cancelBtnText="Cancel"
        submitButtonProps={{
          className: 'ml3',
          disabled: _isNil(selectedStudy),
          loading: false,
        }}
        onCancel={closeModal}
        onSubmit={handleFormSubmit}
        className="pa3 flex self-end bt b--moon-gray w-100"
      />
    </ModalContainer>
  );
};

export default MoveToStudyList;
